import type {
  UpdateQuestionnaireTemplateInput,
  UpdateQuestionnaireTemplateMutation,
  UpdateQuestionnaireTemplateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateQuestionnaireTemplateDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Maps the TRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateQuestionnaireTemplateMutation => ({
  updateQuestionnaireTemplateApi: {
    __typename: 'IdOutput',
    Id: trpcData.Id,
  },
});

export const useUpdateQuestionnaireTemplate = createMutationHook({
  name: 'updateQuestionnaireTemplate',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.questionnaireTemplate.update.mutationOptions({ onSuccess }),
  mapInput: (input: {
    variables: UpdateQuestionnaireTemplateMutationVariables;
  }): UpdateQuestionnaireTemplateInput => {
    const object = input.variables.object;

    return {
      Id: object?.Id ?? '',
      Title: object?.Title ?? '',
      Description: object?.Description,
      CustomAttributeData: object?.CustomAttributeData,
      OwnerUserIds: object?.OwnerUserIds ?? [],
      OwnerGroupIds: object?.OwnerGroupIds ?? [],
      ContributorUserIds: object?.ContributorUserIds ?? [],
      ContributorGroupIds: object?.ContributorGroupIds ?? [],
      ViewerUserIds: object?.ViewerUserIds,
      ViewerGroupIds: object?.ViewerGroupIds,
      TagTypeIds: object?.TagTypeIds ?? [],
      DepartmentTypeIds: object?.DepartmentTypeIds ?? [],
    };
  },
  invalidate: (trpc) => [
    trpc.frontend.questionnaireTemplate.register.queryKey(),
    trpc.frontend.questionnaireTemplate.getById.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateQuestionnaireTemplateMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: UpdateQuestionnaireTemplateDocument,
  graphqlVariables: (input: {
    variables: UpdateQuestionnaireTemplateMutationVariables;
  }) => input.variables,
  evictFields: ['questionnaire_template', 'questionnaire_template_by_pk'],
  refetchQueries: [
    namedOperations.Query.getQuestionnaireTemplateById,
    namedOperations.Query.getQuestionnaireTemplates,
  ],
});
